import 'ante-react-kit/dist/index.css';
import './styles/toast.scss'; // restyle toast (dx notify) tối giản — sau kit CSS để thắng cascade

import React from 'react';
import ReactDOM from 'react-dom/client';
import { AntReact, AntLockScreen, AntAuth, AntLocalization } from 'ante-react-kit';

import appConfig from './config';
import { ensureBootstrapConfig, ensureConfigTables } from './config/load-config';
import './config/init-quick-popups';
import { PickerRegistry } from './components/AntPickers';
import appInfo from './app-info';
import appAssets from './app-assets';
// import appNavigation from './app-navigation';
import appNavigation from './app-navigation-v2';
import appModules from './app-modules';
import appRoutes from './app-routes';

import posNavigation from './pos-navigation';
import accountingNavigation, { accountingModules } from './accounting-navigation';
import accountingQuickCreate, { accountingQuickCreateGroups } from './accounting-quick-create';
import posRoutes from './pos-routes';

import UploadProgressPanel from './pages/media/MEDExplorer/FloatingPanels/UploadProgressPanel';

if (typeof window !== 'undefined') {
    window.__AntPickerRegistry = PickerRegistry;
}

var ar = appNavigation;
if (process.env.REACT_APP_BUSINESS_SHORT_CODE) ar = ar.filter((a) => a.path !== '/setting/sys');

// Thực đơn Kế toán: mục trỏ màn có sẵn (route ngoài /acc/, lấy từ Vận hành/ERP) gắn icon sao.
const STAR_ICON = 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z';
const accNavigation = accountingNavigation.map((x) => (x.path.startsWith('/acc/') ? x : { ...x, icon: STAR_ICON }));

async function startApp() {
    // Preserve compatibility with newly added eager routes until they adopt
    // lazyScreen; missing metadata must never silently change fields or actions.
    const hasEagerRoute = [...appRoutes, ...posRoutes].some((route) => route.component && !route.component.anteConfigLazy);
    await (hasEagerRoute ? ensureConfigTables('*') : ensureBootstrapConfig());
    AntReact.render({
        appNavigation: ar,
        appModules: appModules,
        appInfo: appInfo,
        appAssets: appAssets,
        appRoutes: appRoutes,
        appConfig: appConfig,
        posNavigation: posNavigation,
        accountingNavigation: accNavigation,
        accountingModules: accountingModules,
        accountingQuickCreate: accountingQuickCreate,
        accountingQuickCreateGroups: accountingQuickCreateGroups,
        posRoutes: posRoutes,
        backgroundPrinters: [
            {
                activity: 'PrintAROrder',
                printUrl: '/ar/AROrder/generateBillHTML',
                module: 'ar',
                table: 'AROrder',
                repostFields: [{ dateField: 'order_code', alias: 'code' }, 'remote_event_id']
            },
            {
                activity: 'PrintServingTicket',
                printUrl: '/ar/ARDelivery/generateServingTicketHTML',
                module: 'ar',
                table: 'ARDelivery',
                repostFields: ['virtual_ticket_id', 'remote_event_id']
            },
            {
                activity: 'PrintCookingTicket',
                printUrl: '/ar/ARDelivery/generateFoodTicketHTML',
                module: 'ar',
                table: 'ARDelivery',
                repostFields: ['delivery_id', 'virtual_delivery_id', 'remote_event_id']
            },
            {
                activity: 'PrintCarrierLabel',
                printUrl: '/lg/LGTransport/generateLabelHTML',
                module: 'lg',
                table: 'LGTransport',
                repostFields: ['code', 'remote_event_id']
            }
        ]
    });
}
startApp().catch((error) => {
    console.error('Application configuration could not be loaded', error);
    const button = document.createElement('button');
    button.textContent = AntLocalization.translateText({ vi: 'Tải lại', en: 'Reload', fr: 'Recharger', es: 'Recargar', id: 'Muat ulang', ja: '再読み込み', zh: '重新加载', ko: '새로고침', hi: 'पुनः लोड करें', ar: 'إعادة تحميل' });
    button.onclick = () => window.location.reload();
    document.getElementById('root').replaceChildren(button);
});

// Mount AntLockScreen ở app root — overlay fullscreen khi user lock màn hình hoặc auto-lock theo idle
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    var lockRootEl = document.getElementById('ant-lock-screen-root');
    if (!lockRootEl) {
        lockRootEl = document.createElement('div');
        lockRootEl.id = 'ant-lock-screen-root';
        document.body.appendChild(lockRootEl);
    }
    var lockRoot = ReactDOM.createRoot(lockRootEl);

    function AntLockScreenMount() {
        var [valid, setValid] = React.useState(function () {
            return typeof AntAuth.isValidSession === 'function' ? AntAuth.isValidSession() : false;
        });
        React.useEffect(function () {
            var iv = setInterval(function () {
                var v = typeof AntAuth.isValidSession === 'function' ? AntAuth.isValidSession() : false;
                setValid(function (prev) { return prev !== v ? v : prev; });
            }, 1000);
            return function () { clearInterval(iv); };
        }, []);
        return valid ? React.createElement(AntLockScreen) : null;
    }

    lockRoot.render(React.createElement(AntLockScreenMount));
}

// Mount UploadProgressPanel ở app root — floating panel hiển thị trạng thái upload
// xuyên suốt mọi trang (user navigate đi trang khác vẫn thấy progress).
// Panel tự return null khi queue rỗng nên không ảnh hưởng UI khi không có upload.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    var uploadPanelRootEl = document.getElementById('med-upload-panel-root');
    if (!uploadPanelRootEl) {
        uploadPanelRootEl = document.createElement('div');
        uploadPanelRootEl.id = 'med-upload-panel-root';
        document.body.appendChild(uploadPanelRootEl);
    }
    var uploadPanelRoot = ReactDOM.createRoot(uploadPanelRootEl);
    uploadPanelRoot.render(React.createElement(UploadProgressPanel));
}
